<script setup>
import { reactive, onMounted, ref, defineProps, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import CSATDisplayTypeSelector from './components/CSATDisplayTypeSelector.vue';
import SurveyRuleSelector from './components/SurveyRuleSelector.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WhatsappFlowSettings from '../channels/WhatsappFlowSettings.vue';
import { resolveSurveyType, BOT_ABANDONED_LABEL } from './helpers/surveyType';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');

const isUpdating = ref(false);
const csatLabelValues = ref([]);
const npsLabelValues = ref([]);
// Whether the admin has already made a choice about the NPS rules, so the suggested
// exclusion is offered once and never re-added over a deliberate removal. Derived from
// meaningful state, not from the presence of the `survey_rules` key: `format_nps_config`
// stamps that key on every save of this page, including saves of the CSAT section.
const npsRulesConfigured = ref(false);
// Suppresses a second suggestion within the same session, e.g. after the admin removes the
// label and toggles NPS off and on again without saving.
const npsRuleSuggested = ref(false);

// `survey_type` is the single source of truth on the backend, so the three modes are
// mutually exclusive by construction: turning one on turns the others off.
const state = reactive({
  surveyType: 'none',
  displayType: 'emoji',
  message: '',
  csatRuleOperator: 'contains',
  npsMessage: '',
  npsRuleOperator: 'contains',
  externalSurveyUrl: '',
});

const surveyTypeSwitch = type =>
  computed({
    get: () => state.surveyType === type,
    set: enabled => {
      state.surveyType = enabled ? type : 'none';
    },
  });

const isCsatEnabled = surveyTypeSwitch('csat');
const isNpsEnabled = surveyTypeSwitch('nps');
const isExternalEnabled = surveyTypeSwitch('external');

const isWhatsAppCloudChannel = computed(
  () =>
    props.inbox.channel_type === 'Channel::Whatsapp' &&
    props.inbox.provider === 'whatsapp_cloud'
);

const showCsatFlowSettings = computed(
  () => isWhatsAppCloudChannel.value && isCsatEnabled.value
);

// The NPS survey is only ever answered inside the Flow, so the panel has to be reachable
// before the survey can be turned on — and the toggle stays locked until a flow is live.
const isNpsFlowPublished = ref(false);
const canEnableNps = computed(
  () => isWhatsAppCloudChannel.value && isNpsFlowPublished.value
);
const isNpsToggleDisabled = computed(
  () => !isNpsEnabled.value && !canEnableNps.value
);

const initializeState = () => {
  if (!props.inbox) return;

  const {
    external_survey_url: externalSurveyUrl,
    csat_config: csatConfig,
    nps_config: npsConfig,
  } = props.inbox;

  state.surveyType = resolveSurveyType(props.inbox);
  state.externalSurveyUrl = externalSurveyUrl || '';

  const {
    display_type: displayType = CSAT_DISPLAY_TYPES.EMOJI,
    message = '',
    survey_rules: csatRules = {},
  } = csatConfig || {};

  state.displayType = displayType;
  state.message = message;
  state.csatRuleOperator = csatRules.operator || 'contains';
  csatLabelValues.value = Array.isArray(csatRules.values)
    ? [...csatRules.values]
    : [];

  const { message: npsMessage = '', survey_rules: npsRules = {} } =
    npsConfig || {};

  state.npsMessage = npsMessage;
  state.npsRuleOperator = npsRules.operator || 'contains';
  npsLabelValues.value = Array.isArray(npsRules.values)
    ? [...npsRules.values]
    : [];
  // A rule the admin actually set, or NPS already being the saved mode, both mean the
  // choice was made. An empty rule on an inbox that never ran NPS does not.
  npsRulesConfigured.value =
    npsLabelValues.value.length > 0 || props.inbox.survey_type === 'nps';
};

// Turning NPS on for the first time suggests excluding conversations the bot lost, so the
// survey does not go to customers who never got an answer. Pre-selected rather than
// silently applied: it shows up in the rule picker and can be removed.
watch(isNpsEnabled, enabled => {
  if (
    !enabled ||
    npsRuleSuggested.value ||
    npsRulesConfigured.value ||
    npsLabelValues.value.length
  ) {
    return;
  }

  npsRuleSuggested.value = true;
  state.npsRuleOperator = 'does_not_contain';
  npsLabelValues.value = [BOT_ABANDONED_LABEL];
});

onMounted(() => {
  initializeState();
  if (!labels.value?.length) store.dispatch('labels/get');
});

watch(() => props.inbox, initializeState, { immediate: true });

const updateDisplayType = type => {
  state.displayType = type;
};

const saveSettings = async () => {
  try {
    isUpdating.value = true;

    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      survey_type: state.surveyType,
      external_survey_url: state.externalSurveyUrl,
      csat_config: {
        display_type: state.displayType,
        message: state.message,
        survey_rules: {
          operator: state.csatRuleOperator,
          values: csatLabelValues.value,
        },
      },
      nps_config: {
        message: state.npsMessage,
        survey_rules: {
          operator: state.npsRuleOperator,
          values: npsLabelValues.value,
        },
      },
    });

    useAlert(t('INBOX_MGMT.CSAT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.CSAT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('INBOX_MGMT.CSAT.TITLE')"
      :description="$t('INBOX_MGMT.CSAT.SUBTITLE')"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="isCsatEnabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <WithLabel
          :label="$t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL')"
          name="display_type"
        >
          <CSATDisplayTypeSelector
            :selected-type="state.displayType"
            @update="updateDisplayType"
          />
        </WithLabel>

        <WithLabel :label="$t('INBOX_MGMT.CSAT.MESSAGE.LABEL')" name="message">
          <Editor
            v-model="state.message"
            :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
            :max-length="200"
            class="w-full"
          />
        </WithLabel>

        <WithLabel
          :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL')"
          name="survey_rule"
        >
          <SurveyRuleSelector
            v-model:operator="state.csatRuleOperator"
            v-model:values="csatLabelValues"
          />
        </WithLabel>
        <p class="text-sm italic text-n-slate-11">
          {{ $t('INBOX_MGMT.CSAT.NOTE') }}
        </p>
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>

    <WhatsappFlowSettings
      v-if="showCsatFlowSettings"
      :inbox="inbox"
      flow-type="csat"
      class="mt-8"
    />

    <SectionLayout
      :title="$t('INBOX_MGMT.NPS.TITLE')"
      :description="$t('INBOX_MGMT.NPS.SUBTITLE')"
      class="mt-8"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="isNpsEnabled" :disabled="isNpsToggleDisabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <p v-if="!isWhatsAppCloudChannel" class="text-sm text-n-amber-11">
          {{ $t('INBOX_MGMT.NPS.REQUIRES_WHATSAPP_CLOUD') }}
        </p>
        <p v-else-if="!isNpsFlowPublished" class="text-sm text-n-amber-11">
          {{ $t('INBOX_MGMT.NPS.REQUIRES_PUBLISHED_FLOW') }}
        </p>

        <WithLabel
          :label="$t('INBOX_MGMT.NPS.MESSAGE.LABEL')"
          name="nps_message"
        >
          <Editor
            v-model="state.npsMessage"
            :placeholder="$t('INBOX_MGMT.NPS.MESSAGE.PLACEHOLDER')"
            :max-length="200"
            class="w-full"
          />
        </WithLabel>

        <WithLabel
          :label="$t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL')"
          name="nps_survey_rule"
        >
          <SurveyRuleSelector
            v-model:operator="state.npsRuleOperator"
            v-model:values="npsLabelValues"
          />
        </WithLabel>
        <p class="text-sm italic text-n-slate-11">
          {{ $t('INBOX_MGMT.NPS.NOTE') }}
        </p>
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>

    <WhatsappFlowSettings
      v-if="isWhatsAppCloudChannel"
      :inbox="inbox"
      flow-type="nps"
      class="mt-8"
      @status-change="published => (isNpsFlowPublished = published)"
    />

    <SectionLayout
      :title="$t('INBOX_MGMT.EXTERNAL_SURVEY.TITLE')"
      :description="$t('INBOX_MGMT.EXTERNAL_SURVEY.SUBTITLE')"
      class="mt-8"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="isExternalEnabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <Input
          v-model="state.externalSurveyUrl"
          :label="$t('INBOX_MGMT.EXTERNAL_SURVEY.URL.LABEL')"
          :placeholder="$t('INBOX_MGMT.EXTERNAL_SURVEY.URL.PLACEHOLDER')"
          type="url"
          class="w-full"
        />
        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
